import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8 } from "styled-components";
import React from "react";
import { gsap } from "gsap";
import { hasItems } from "~/features/app/utils";
import type { Mode } from "~/features/ui/get-theme-style";
import { Wrap } from "~/features/ui/wrap";
import { Slider } from "~/features/ui/slider";
import { Progress } from "~/features/ui/slider/progress";
import { Filter } from "./filter";
import { SectionHeader } from "./header";
import { Individual } from "./individual";
import { Pair } from "./pair";
import { Default } from "./default";
const EASE = "cubic-bezier(0.125, 0.425, 0.27, 1)";
export type Props = {
  headline?: string;
  text?: string;
  link?: React.ReactNode;
  children: React.ReactNode[];
  theme?: "white" | "grey";
  mode: Mode;
  mobileLayout?: "slider" | "grid";
  hasTopDivider?: boolean;
  isHighlighted?: boolean;
  generations?: (string | null)[];
};
const Section = _styled.section.withConfig({
  componentId: "sc-sf-mnvz05-0"
})`
  ${{
  "overflow": "hidden"
}}

  .splide__track {
    overflow: visible !important;
  }
`;
var _StyledSection = _styled2(Section).withConfig({
  componentId: "sc-sf-mnvz05-1"
})`${p => p.$_css}`;
export function AdaptiveSection(props: Props & React.ComponentPropsWithRef<"section">) {
  const {
    headline,
    text,
    children,
    mode,
    link,
    hasTopDivider,
    isHighlighted = false,
    generations,
    ...rest
  } = props;
  const [activeSlideIndex, setActiveSlideIndex] = React.useState(0);
  const [mobileGeneration, setMobileGeneration] = React.useState("");
  const mobileSliderRef = React.useRef<HTMLDivElement>(null);
  const mobileAnimationRef = React.useRef<gsap.core.Tween | null>(null);
  const isFirstMobileRender = React.useRef(true);
  const generationOptions = React.useMemo(() => {
    if (!generations) return [];
    const unique = [...new Set(generations.filter(Boolean))] as string[];
    if (unique.length <= 1) return [];
    return [{
      label: "All",
      value: ""
    }, ...unique.map(g => ({
      label: g,
      value: g
    }))];
  }, [generations]);
  const mobileItems = React.useMemo(() => {
    if (!mobileGeneration || !generations) return children;
    return children.filter((_, index) => generations[index] === mobileGeneration);
  }, [children, generations, mobileGeneration]);
  const handleMobileGenerationChange = (value: string) => {
    setMobileGeneration(value);
    setActiveSlideIndex(0);
  };
  React.useEffect(() => {
    if (isFirstMobileRender.current) {
      isFirstMobileRender.current = false;
      return;
    }
    mobileAnimationRef.current?.kill();
    const container = mobileSliderRef.current;
    if (!container) return;
    requestAnimationFrame(() => {
      const slides = container.querySelectorAll(".splide__slide");
      if (slides.length === 0) return;
      mobileAnimationRef.current = gsap.fromTo(slides, {
        y: 20,
        opacity: 0
      }, {
        y: 0,
        opacity: 1,
        duration: 0.5,
        ease: EASE,
        stagger: 0.08
      });
    });
  }, [mobileGeneration]);
  React.useEffect(() => {
    return () => {
      mobileAnimationRef.current?.kill();
    };
  }, []);
  const hasHeaderData = Boolean(headline || text || link);
  if (!hasItems(children)) {
    return null;
  }
  const items = children;
  return <_StyledSection data-theme={mode} {...rest} $_css={[hasTopDivider && {
    "borderTopWidth": "1px",
    "borderColor": "var(--stroke-primary)"
  }, {
    "position": "relative",
    "backgroundColor": "var(--bg-secondary)",
    "color": "var(--text-primary)"
  }, isHighlighted && {
    "backgroundColor": "var(--white-static)"
  }]}>
      <_StyledWrap>
        {items.length === 1 && <Individual headline={headline} text={text} link={link} hasHeaderData={hasHeaderData}>
            {[items[0]]}
          </Individual>}
        {items.length === 2 && <Pair headline={headline} text={text} link={link} hasHeaderData={hasHeaderData}>
            {[items[0], items[1]]}
          </Pair>}
        {items.length >= 3 && <Default hasHeaderData={hasHeaderData} headline={headline} text={text} link={link} generations={generations}>
            {items}
          </Default>}

        {/* Mobile slider */}
        <_StyledDiv $_css2={[{
        "position": "relative",
        "zIndex": "10",
        "borderColor": "var(--stroke-primary)",
        "@media (min-width: 1024px)": {
          "display": "none"
        }
      }]}>
          {hasHeaderData && <_StyledDiv2 $_css3={[{
          "padding": "var(--spacing-xl)"
        }]}>
              <SectionHeader alignment="vertical" headline={headline} text={text} link={link} />
            </_StyledDiv2>}

          {hasItems(generationOptions) && <_StyledDiv3 $_css4={[{
          "borderTopWidth": "1px",
          "borderColor": "var(--stroke-primary)"
        }]}>
              <Filter options={generationOptions} value={mobileGeneration} onChange={handleMobileGenerationChange} />
            </_StyledDiv3>}

          <_StyledDiv4 ref={mobileSliderRef} $_css5={[{
          "borderTopWidth": "1px",
          "borderColor": "var(--stroke-primary)",
          "paddingLeft": "var(--spacing-md)",
          "paddingRight": "var(--spacing-md)",
          "paddingBottom": "var(--spacing-xl)",
          "paddingTop": "var(--spacing-md)"
        }]}>
            <Slider onMove={splide => setActiveSlideIndex(splide.Components.Controller.getIndex())} options={{
            fixedWidth: mobileItems.length === 1 ? "100%" : "95%",
            gap: 16
          }}>
              {mobileItems.map((child, idx) => <Slider.Slide key={idx}>{child}</Slider.Slide>)}
            </Slider>
          </_StyledDiv4>

          {mobileItems.length > 1 && <_StyledDiv5 $_css6={[{
          "paddingLeft": "var(--spacing-md)",
          "paddingRight": "var(--spacing-md)",
          "paddingTop": "var(--spacing-lg)",
          "paddingBottom": "var(--spacing-lg)"
        }]}>
              <Progress activeSlideIndex={activeSlideIndex} totalSlides={mobileItems.length} />
            </_StyledDiv5>}
        </_StyledDiv>
      </_StyledWrap>
    </_StyledSection>;
}
var _StyledWrap = _styled3(Wrap).withConfig({
  componentId: "sc-sf-mnvz05-2"
})({
  "borderWidth": "0px"
});
var _StyledDiv = _styled4("div").withConfig({
  componentId: "sc-sf-mnvz05-3"
})`${p => p.$_css2}`;
var _StyledDiv2 = _styled5("div").withConfig({
  componentId: "sc-sf-mnvz05-4"
})`${p => p.$_css3}`;
var _StyledDiv3 = _styled6("div").withConfig({
  componentId: "sc-sf-mnvz05-5"
})`${p => p.$_css4}`;
var _StyledDiv4 = _styled7("div").withConfig({
  componentId: "sc-sf-mnvz05-6"
})`${p => p.$_css5}`;
var _StyledDiv5 = /*#__PURE__*/_styled8("div").withConfig({
  displayName: "adaptive-section___StyledDiv5",
  componentId: "sc-1bpgdxw-0"
})(["", ""], p => p.$_css6);